<script setup lang="ts">
// 調整レイヤーのプロパティ。スライダーのドラッグ中はトランザクションで 1 履歴にまとめる。
import { computed, ref } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n, type I18nKey } from '@/i18n'
import { defaultAdjustment } from '@/model/defaults'
import type { Adjustment, AdjustmentLayer, CurvePoints, LevelsChannel, SelectiveColorRange } from '@/types/document'
import { SELECTIVE_COLOR_RANGES } from '@/types/document'
import { builtinCube, parseCube, LUT_PRESETS, type LutPreset } from '@/render/adjust/cube'
import { pickFiles } from '@/io/download'
import { toast } from '@/composables/useToast'
import SliderField from '@/components/common/SliderField.vue'
import GradientEditor from '@/components/common/GradientEditor.vue'
import CurveEditor from '@/components/common/CurveEditor.vue'

const props = defineProps<{ layer: AdjustmentLayer }>()
const docStore = useDocumentStore()
const { t } = useI18n()
const adj = computed(() => props.layer.adjustment)

let inTx = false
function begin() {
  if (!inTx) {
    docStore.beginTransaction('history.adjust')
    inTx = true
  }
}
function end() {
  if (inTx) {
    docStore.commitTransaction()
    inTx = false
  }
}
function set(next: Adjustment, commit: boolean) {
  if (!commit) begin()
  docStore.setAdjustment(props.layer.id, next)
  if (commit) end()
}
function patch<K extends Adjustment>(p: Partial<K>, commit: boolean) {
  set({ ...(adj.value as K), ...p } as Adjustment, commit)
}
function reset() {
  set(defaultAdjustment(adj.value.kind), true)
}
const channel = computed({
  get: () => (ui.channel as 'rgb' | 'r' | 'g' | 'b'),
  set: v => (ui.channel = v)
})
const ui = { channel: 'rgb' as string }

function levelsOf(): LevelsChannel {
  const a = adj.value as Extract<Adjustment, { kind: 'levels' }>
  return (channel.value === 'rgb' ? a.rgb : a[channel.value as 'r']) ?? { inBlack: 0, inWhite: 255, gamma: 1, outBlack: 0, outWhite: 255 }
}
function setLevels(p: Partial<LevelsChannel>, commit: boolean) {
  const a = adj.value as Extract<Adjustment, { kind: 'levels' }>
  const cur = levelsOf()
  const next = { ...cur, ...p }
  set({ ...a, [channel.value]: next }, commit)
}
function curveOf(): CurvePoints {
  const a = adj.value as Extract<Adjustment, { kind: 'curves' }>
  return (channel.value === 'rgb' ? a.rgb : a[channel.value as 'r']) ?? [[0, 0], [255, 255]]
}
function setCurve(pts: CurvePoints, commit: boolean) {
  const a = adj.value as Extract<Adjustment, { kind: 'curves' }>
  set({ ...a, [channel.value]: pts.map(p => [p[0], p[1]] as [number, number]) }, commit)
}
const hs = computed(() => adj.value as Extract<Adjustment, { kind: 'hue-saturation' }>)
function setHs(p: Partial<{ hue: number; saturation: number; lightness: number }>, commit: boolean) {
  set({ ...hs.value, master: { ...hs.value.master, ...p } }, commit)
}
const sc = computed(() => adj.value as Extract<Adjustment, { kind: 'selective-color' }>)
function applyPreset(name: string) {
  if (!name) return
  const cube = builtinCube(name as LutPreset)
  docStore.setColorLookup(props.layer.id, cube, t(`adj.lut.${name}` as I18nKey))
}
async function loadCube() {
  const files = await pickFiles('.cube,.CUBE')
  const f = files[0]
  if (!f) return
  try {
    const cube = parseCube(await f.text())
    docStore.setColorLookup(props.layer.id, cube, cube.title ?? f.name.replace(/\.cube$/i, ''))
  } catch (e) {
    toast.error(e instanceof Error ? e.message : String(e))
  }
}
const selRange = ref<SelectiveColorRange>('reds')
function setSel(i: number, v: number, commit: boolean) {
  const cur = sc.value
  const q = [...cur.ranges[selRange.value]] as [number, number, number, number]
  q[i] = v
  set({ ...cur, ranges: { ...cur.ranges, [selRange.value]: q } }, commit)
}
function tri(key: 'shadows' | 'midtones' | 'highlights', i: number, v: number, commit: boolean) {
  const a = adj.value as Extract<Adjustment, { kind: 'color-balance' }>
  const arr = [...a[key]] as [number, number, number]
  arr[i] = v
  set({ ...a, [key]: arr }, commit)
}
function mixer(row: 'r' | 'g' | 'b', i: number, v: number, commit: boolean) {
  const a = adj.value as Extract<Adjustment, { kind: 'channel-mixer' }>
  const arr = [...a[row]] as [number, number, number, number]
  arr[i] = v
  set({ ...a, [row]: arr }, commit)
}
</script>

<template>
  <div class="adj">
    <div class="row head">
      <span class="kind">{{ t(`adj.${adj.kind}` as I18nKey) }}</span>
      <span class="spacer" />
      <button class="ghost" @click="reset">{{ t('adj.reset') }}</button>
    </div>

    <template v-if="adj.kind === 'levels' || adj.kind === 'curves'">
      <label class="row"><span class="muted">{{ t('adj.channel') }}</span>
        <select v-model="channel"><option value="rgb">RGB</option><option value="r">R</option><option value="g">G</option><option value="b">B</option></select>
      </label>
    </template>

    <template v-if="adj.kind === 'levels'">
      <div class="muted small">{{ t('adj.inputLevels') }}</div>
      <SliderField :label="'黒'" :value="levelsOf().inBlack" :min="0" :max="253" @input="v => setLevels({ inBlack: Math.min(v, levelsOf().inWhite - 2) }, false)" @change="v => setLevels({ inBlack: Math.min(v, levelsOf().inWhite - 2) }, true)" />
      <SliderField :label="t('adj.gamma')" :value="levelsOf().gamma" :min="0.1" :max="9.99" :step="0.01" @input="v => setLevels({ gamma: v }, false)" @change="v => setLevels({ gamma: v }, true)" />
      <SliderField :label="'白'" :value="levelsOf().inWhite" :min="2" :max="255" @input="v => setLevels({ inWhite: Math.max(v, levelsOf().inBlack + 2) }, false)" @change="v => setLevels({ inWhite: Math.max(v, levelsOf().inBlack + 2) }, true)" />
      <div class="muted small">{{ t('adj.outputLevels') }}</div>
      <SliderField :label="'黒'" :value="levelsOf().outBlack" :min="0" :max="255" @input="v => setLevels({ outBlack: v }, false)" @change="v => setLevels({ outBlack: v }, true)" />
      <SliderField :label="'白'" :value="levelsOf().outWhite" :min="0" :max="255" @input="v => setLevels({ outWhite: v }, false)" @change="v => setLevels({ outWhite: v }, true)" />
    </template>

    <template v-else-if="adj.kind === 'curves'">
      <CurveEditor :points="curveOf()" @input="pts => setCurve(pts, false)" @change="pts => setCurve(pts, true)" />
    </template>

    <template v-else-if="adj.kind === 'brightness-contrast'">
      <SliderField :label="t('adj.brightness')" :value="adj.brightness" :min="-150" :max="150" @input="v => patch({ brightness: v }, false)" @change="v => patch({ brightness: v }, true)" />
      <SliderField :label="t('adj.contrast')" :value="adj.contrast" :min="-100" :max="100" @input="v => patch({ contrast: v }, false)" @change="v => patch({ contrast: v }, true)" />
    </template>

    <template v-else-if="adj.kind === 'exposure'">
      <SliderField :label="t('adj.exposure')" :value="adj.exposure" :min="-20" :max="20" :step="0.01" @input="v => patch({ exposure: v }, false)" @change="v => patch({ exposure: v }, true)" />
      <SliderField :label="t('adj.offset')" :value="adj.offset" :min="-0.5" :max="0.5" :step="0.001" @input="v => patch({ offset: v }, false)" @change="v => patch({ offset: v }, true)" />
      <SliderField :label="t('adj.gamma')" :value="adj.gamma" :min="0.01" :max="9.99" :step="0.01" @input="v => patch({ gamma: v }, false)" @change="v => patch({ gamma: v }, true)" />
    </template>

    <template v-else-if="adj.kind === 'hue-saturation'">
      <label class="row"><input type="checkbox" :checked="adj.colorize" @change="patch({ colorize: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.colorize') }}</span></label>
      <template v-if="!adj.colorize">
        <SliderField :label="t('adj.hue')" :value="hs.master.hue" :min="-180" :max="180" @input="v => setHs({ hue: v }, false)" @change="v => setHs({ hue: v }, true)" />
        <SliderField :label="t('adj.saturation')" :value="hs.master.saturation" :min="-100" :max="100" @input="v => setHs({ saturation: v }, false)" @change="v => setHs({ saturation: v }, true)" />
        <SliderField :label="t('adj.lightness')" :value="hs.master.lightness" :min="-100" :max="100" @input="v => setHs({ lightness: v }, false)" @change="v => setHs({ lightness: v }, true)" />
      </template>
      <template v-else>
        <SliderField :label="t('adj.hue')" :value="adj.colorizeHue" :min="0" :max="360" @input="v => patch({ colorizeHue: v }, false)" @change="v => patch({ colorizeHue: v }, true)" />
        <SliderField :label="t('adj.saturation')" :value="adj.colorizeSaturation" :min="0" :max="100" @input="v => patch({ colorizeSaturation: v }, false)" @change="v => patch({ colorizeSaturation: v }, true)" />
        <SliderField :label="t('adj.lightness')" :value="adj.colorizeLightness" :min="-100" :max="100" @input="v => patch({ colorizeLightness: v }, false)" @change="v => patch({ colorizeLightness: v }, true)" />
      </template>
    </template>

    <template v-else-if="adj.kind === 'color-lookup'">
      <div class="row"><span class="muted">{{ t('adj.lut.current') }}</span><span class="mono">{{ adj.name || t('adj.lut.none') }}</span></div>
      <label class="row"><span class="muted">{{ t('adj.lut.preset') }}</span>
        <select :value="''" @change="applyPreset(($event.target as HTMLSelectElement).value)">
          <option value="">—</option>
          <option v-for="p in LUT_PRESETS" :key="p" :value="p">{{ t(`adj.lut.${p}` as I18nKey) }}</option>
        </select>
      </label>
      <button @click="loadCube">{{ t('adj.lut.load') }}</button>
    </template>
    <template v-else-if="adj.kind === 'selective-color'">
      <label class="row"><span class="muted">{{ t('adj.sel.range') }}</span>
        <select v-model="selRange">
          <option v-for="r in SELECTIVE_COLOR_RANGES" :key="r" :value="r">{{ t(`adj.sel.${r}` as I18nKey) }}</option>
        </select>
      </label>
      <SliderField v-for="(lbl, i) in ['C', 'M', 'Y', 'K']" :key="lbl" :label="lbl" :value="sc.ranges[selRange][i]!" :min="-100" :max="100" unit="%" @input="v => setSel(i, v, false)" @change="v => setSel(i, v, true)" />
      <label class="row"><input type="checkbox" :checked="sc.relative" @change="patch({ relative: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.relative') }}</span></label>
    </template>

    <template v-else-if="adj.kind === 'color-balance'">
      <template v-for="key in (['shadows', 'midtones', 'highlights'] as const)" :key="key">
        <div class="muted small">{{ t(`adj.${key}` as I18nKey) }}</div>
        <SliderField v-for="(lbl, i) in ['C – R', 'M – G', 'Y – B']" :key="lbl" :label="lbl" :value="adj[key][i]!" :min="-100" :max="100" @input="v => tri(key, i, v, false)" @change="v => tri(key, i, v, true)" />
      </template>
      <label class="row"><input type="checkbox" :checked="adj.preserveLuminosity" @change="patch({ preserveLuminosity: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.preserveLum') }}</span></label>
    </template>

    <template v-else-if="adj.kind === 'black-white'">
      <SliderField v-for="key in (['reds', 'yellows', 'greens', 'cyans', 'blues', 'magentas'] as const)" :key="key" :label="t(`adj.${key}` as I18nKey)" :value="adj[key]" :min="-200" :max="300" @input="v => patch({ [key]: v }, false)" @change="v => patch({ [key]: v }, true)" />
      <label class="row"><input type="checkbox" :checked="adj.tint" @change="patch({ tint: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.tint') }}</span><input type="color" :value="adj.tintHex" @change="patch({ tintHex: ($event.target as HTMLInputElement).value }, true)" /></label>
    </template>

    <template v-else-if="adj.kind === 'photo-filter'">
      <label class="row"><span class="muted">{{ t('adj.filterColor') }}</span><input type="color" :value="adj.hex" @change="patch({ hex: ($event.target as HTMLInputElement).value }, true)" /></label>
      <SliderField :label="t('adj.density')" :value="adj.density" :min="0" :max="100" unit="%" @input="v => patch({ density: v }, false)" @change="v => patch({ density: v }, true)" />
      <label class="row"><input type="checkbox" :checked="adj.preserveLuminosity" @change="patch({ preserveLuminosity: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.preserveLum') }}</span></label>
    </template>

    <template v-else-if="adj.kind === 'channel-mixer'">
      <template v-for="row in (['r', 'g', 'b'] as const)" :key="row">
        <div v-if="!adj.monochrome || row === 'r'" class="muted small">{{ t('adj.outputChannel') }}: {{ row.toUpperCase() }}</div>
        <template v-if="!adj.monochrome || row === 'r'">
          <SliderField v-for="(lbl, i) in ['R', 'G', 'B', t('adj.constant')]" :key="lbl" :label="lbl" :value="adj[row][i]!" :min="-200" :max="200" unit="%" @input="v => mixer(row, i, v, false)" @change="v => mixer(row, i, v, true)" />
        </template>
      </template>
      <label class="row"><input type="checkbox" :checked="adj.monochrome" @change="patch({ monochrome: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.monochrome') }}</span></label>
    </template>

    <template v-else-if="adj.kind === 'posterize'">
      <SliderField :label="t('adj.levelsCount')" :value="adj.levels" :min="2" :max="255" @input="v => patch({ levels: v }, false)" @change="v => patch({ levels: v }, true)" />
    </template>

    <template v-else-if="adj.kind === 'threshold'">
      <SliderField :label="t('adj.thresholdLevel')" :value="adj.level" :min="1" :max="255" @input="v => patch({ level: v }, false)" @change="v => patch({ level: v }, true)" />
    </template>

    <template v-else-if="adj.kind === 'gradient-map'">
      <GradientEditor :stops="adj.stops" @input="st => patch({ stops: st.map(s => ({ pos: s.pos, hex: s.hex })) }, false)" @change="st => patch({ stops: st.map(s => ({ pos: s.pos, hex: s.hex })) }, true)" />
      <label class="row"><input type="checkbox" :checked="adj.reverse" @change="patch({ reverse: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.reverse') }}</span></label>
    </template>

    <template v-else-if="adj.kind === 'vibrance'">
      <SliderField :label="t('adj.vibrance')" :value="adj.vibrance" :min="-100" :max="100" @input="v => patch({ vibrance: v }, false)" @change="v => patch({ vibrance: v }, true)" />
      <SliderField :label="t('adj.saturation')" :value="adj.saturation" :min="-100" :max="100" @input="v => patch({ saturation: v }, false)" @change="v => patch({ saturation: v }, true)" />
    </template>
  </div>
</template>

<style scoped>
.adj {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.head .kind {
  font-weight: 600;
}
.small {
  font-size: 10px;
  margin-top: 4px;
}
</style>
